// src/types/index.ts
function faiCategoryLabel(value) {
  const raw = (value ?? "").trim().toUpperCase().replace(/\s+/g, " ");
  if (raw === "1" || raw === "CAT 1" || raw === "CAT1" || raw === "CAT_1") return "Cat 1";
  if (raw === "2" || raw === "CAT 2" || raw === "CAT2" || raw === "CAT_2") return "Cat 2";
  if (raw === "NONE" || raw === "NO CAT" || raw === "NO_CAT" || raw === "NOCAT")
    return "No category";
  return null;
}
var ROLES = [
  "SUPER_ADMIN",
  "PLATFORM_SUPPORT",
  "PLATFORM_DEVELOPER",
  "COMPETITION_DIRECTOR",
  "CHIEF_JUDGE",
  "JUDGE",
  "SCOREKEEPER",
  "LAUNCH_MARSHAL",
  "GOAL_MARSHAL",
  "ANNOUNCER",
  "DISPLAY_OPERATOR",
  "PUBLIC_USER"
];
var DEFAULT_FAI_2022_RULES = {
  version: "FAI_2022",
  bullseyeScoreCm: 0,
  maximumScoreCm: 1e3,
  /** How many worst scores to drop. 0 keeps every round. */
  discardWorstRounds: 1,
  /** First round at which the worst score is removed. Section 7 uses 5. */
  discardAfterRounds: 5,
  allowReflights: true,
  maxReflightsPerRound: 1,
  teamSize: 4,
  teamScoringPilots: 3,
  teamAllowReserves: true,
  teamMaxReserves: 1,
  womenCategoryEnabled: true,
  juniorCategoryEnabled: true,
  juniorMaxAge: 25,
  maxScoreResultTypes: ["DNF", "ABS", "DNS", "MAXIMUM"],
  excludeFromRankingTypes: ["REFLIGHT"],
  tieBreakPriority: [
    "MOST_BULLSEYES",
    "BEST_SINGLE_SCORE",
    "BEST_LAST_ROUND",
    "MOST_ROUNDS_FLOWN",
    "LOWEST_DISCARDED",
    "PILOT_NUMBER"
  ]
};

// src/schemas/index.ts
import { z } from "zod";
var optionalString = z.union([z.string(), z.null(), z.undefined()]).transform((v) => v == null || v === "" ? void 0 : v);
var loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8)
});
var registerParticipantSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100)
});
var claimPersonByIdentitySchema = z.object({
  aeroJudgeId: optionalString,
  civlId: optionalString,
  personId: optionalString
}).superRefine((val, ctx) => {
  if (!val.aeroJudgeId && !val.civlId && !val.personId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Provide personId, aeroJudgeId, or civlId",
      path: ["personId"]
    });
  }
});
var authenticatedPilotRegistrationSchema = z.object({
  club: optionalString,
  glider: optionalString,
  harness: optionalString,
  emergencyContact: optionalString,
  emergencyPhone: optionalString,
  // First-time profile / fallback only
  firstName: optionalString,
  lastName: optionalString,
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).optional(),
  countryCode: z.union([
    z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use a 3-letter country code"),
    z.literal(""),
    z.undefined()
  ]).transform((v) => v == null || v === "" ? void 0 : v).optional(),
  nationality: optionalString,
  faiLicense: optionalString,
  civlId: optionalString,
  dateOfBirth: z.preprocess(
    (v) => v == null || v === "" ? void 0 : v,
    z.union([z.string().datetime(), z.coerce.date(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).optional()
  )
});
var createCompetitionSchema = z.object({
  name: z.string().min(3).max(200),
  code: z.string().min(3).max(50).regex(/^[A-Z0-9-]+$/i),
  organizer: z.string().min(2),
  venue: z.string().min(2),
  country: z.string().min(2),
  location: z.string().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  startDate: z.string().datetime().or(z.coerce.date()),
  endDate: z.string().datetime().or(z.coerce.date()),
  practiceDays: z.number().int().min(0).default(0),
  officialDays: z.number().int().min(1).default(1),
  maxRounds: z.number().int().min(1).max(30).default(8),
  practiceRounds: z.number().int().min(0).default(2),
  targetDiameterCm: z.number().int().min(50).max(500).default(200),
  /** Out-of-target / DNF / ABS / DNS score in centimetres – competition-specific */
  maximumScoreCm: z.number().min(1).max(1e4).default(1e3),
  ruleSet: z.enum(["FAI_2022", "FAI_FUTURE", "NPHA_LOCAL", "CUSTOM"]).default("FAI_2022"),
  /** `1` and `2` are FAI category events. `NONE` is not a category event. */
  faiCategory: z.enum(["1", "2", "NONE"]).default("2"),
  /** Owning organization; omitted → default/active organization. */
  organizationId: z.string().min(1).optional()
});
var pilotStatusSchema = z.enum([
  "REGISTERED",
  "CONFIRMED",
  "CHECKED_IN",
  "ACTIVE",
  "REJECTED",
  "WITHDRAWN",
  "DISQUALIFIED",
  "DNS"
]);
var createPilotBaseSchema = z.object({
  /**
   * Blank or null clears the bib. A missing field leaves the current number unchanged.
   */
  pilotNumber: z.preprocess((v) => {
    if (v === void 0) return void 0;
    if (v === null || v === "") return null;
    if (typeof v === "number" && !Number.isFinite(v)) return null;
    return v;
  }, z.number().int().positive().nullable().optional()),
  /** Link to existing Person (returning participant). When set, identity can be omitted. */
  personId: optionalString,
  faiLicense: optionalString,
  civlId: optionalString,
  firstName: z.string().min(1).optional().or(z.literal("")).transform((v) => v || void 0),
  lastName: z.string().min(1).optional().or(z.literal("")).transform((v) => v || void 0),
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).default("MALE"),
  nationality: optionalString,
  countryId: optionalString,
  club: optionalString,
  dateOfBirth: z.preprocess(
    (v) => v == null || v === "" ? void 0 : v,
    z.union([z.string().datetime(), z.coerce.date()]).optional()
  ),
  glider: optionalString,
  harness: optionalString,
  reserveStatus: optionalString,
  emergencyContact: optionalString,
  emergencyPhone: optionalString,
  medicalNotes: optionalString,
  /**
   * Organizer create defaults to CONFIRMED on the server when omitted.
   * Public self-registration always uses REGISTERED.
   */
  status: pilotStatusSchema.optional()
});
var createPilotSchema = createPilotBaseSchema.superRefine((val, ctx) => {
  if (val.personId) return;
  if (!val.firstName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "First name is required",
      path: ["firstName"]
    });
  }
  if (!val.lastName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Last name is required",
      path: ["lastName"]
    });
  }
});
var updatePilotSchema = createPilotBaseSchema.partial();
var updatePilotStatusSchema = z.object({
  status: pilotStatusSchema
});
var bulkPilotActionSchema = z.object({
  action: z.enum(["accept", "reject", "remove"]),
  pilotIds: z.array(z.string().min(1)).min(1).max(500)
});
var publicPilotRegistrationSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).default("MALE"),
  /** ISO 3166-1 alpha-3 (e.g. IND, NPL) */
  countryCode: z.union([
    z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use a 3-letter country code"),
    z.literal(""),
    z.undefined()
  ]).transform((v) => v == null || v === "" ? void 0 : v).optional(),
  nationality: optionalString,
  faiLicense: optionalString,
  civlId: optionalString,
  club: optionalString,
  dateOfBirth: z.preprocess(
    (v) => v == null || v === "" ? void 0 : v,
    z.union([z.string().datetime(), z.coerce.date(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).optional()
  ),
  glider: optionalString,
  harness: optionalString,
  emergencyContact: optionalString,
  emergencyPhone: optionalString
});
var createTeamSchema = z.object({
  name: z.string().min(2),
  type: z.enum(["NATIONAL", "CLUB", "WOMEN", "MIXED", "OPEN", "CUSTOM"]).default("NATIONAL"),
  countryId: z.string().optional(),
  memberPilotIds: z.array(z.string()).optional(),
  captainId: z.string().optional(),
  viceCaptainId: z.string().optional()
});
var updateTeamSchema = z.object({
  name: z.string().trim().min(2, "Team name must be at least 2 characters"),
  type: z.enum(["NATIONAL", "CLUB", "WOMEN", "MIXED", "OPEN", "CUSTOM"]).optional()
});
var createRoundSchema = z.object({
  number: z.number().int().positive(),
  name: z.string().optional(),
  type: z.enum(["PRACTICE", "OFFICIAL", "REFLIGHT", "RESTART"]).default("OFFICIAL"),
  orderType: z.enum(["RANDOM", "SEEDED", "MANUAL", "REVERSE"]).default("RANDOM"),
  scheduledAt: z.string().datetime().or(z.coerce.date()).optional()
});
var updateRoundTypeSchema = z.object({
  name: z.string().trim().min(1, "Round name is required").max(80).optional(),
  type: z.enum(["PRACTICE", "OFFICIAL", "REFLIGHT", "RESTART"]).optional()
}).refine((value) => value.name !== void 0 || value.type !== void 0, {
  message: "Provide a round name or type"
});
var enterScoreSchema = z.object({
  flightId: z.string().min(1),
  distanceCm: z.number().min(0).max(1e4).nullable(),
  resultType: z.enum(["MEASURED", "BULLSEYE", "MAXIMUM", "DNF", "ABS", "DNS", "DSQ", "REFLIGHT", "PENALTY"]).default("MEASURED"),
  penaltyCm: z.number().min(0).default(0),
  judgeNotes: z.string().optional()
}).superRefine((value, ctx) => {
  if (value.resultType !== "REFLIGHT") return;
  const reason = value.judgeNotes?.trim() ?? "";
  if (!reason) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["judgeNotes"],
      message: "A reflight reason is required"
    });
  } else if (reason.length > 1e3) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["judgeNotes"],
      message: "Reason must be 1000 characters or fewer"
    });
  }
});
var createUserSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: z.enum([
    "SUPER_ADMIN",
    "PLATFORM_SUPPORT",
    "PLATFORM_DEVELOPER",
    "COMPETITION_DIRECTOR",
    "CHIEF_JUDGE",
    "JUDGE",
    "SCOREKEEPER",
    "LAUNCH_MARSHAL",
    "GOAL_MARSHAL",
    "ANNOUNCER",
    "DISPLAY_OPERATOR",
    "PUBLIC_USER"
  ]),
  phone: z.string().optional()
});
var setUserPasswordSchema = z.object({
  password: z.string().min(8).max(128),
  confirmPassword: z.string().min(8).max(128)
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var forgotPasswordSchema = z.object({
  email: z.string().email()
});
var resetPasswordSchema = z.object({
  token: z.string().min(32).max(256),
  password: z.string().min(8).max(128),
  confirmPassword: z.string().min(8).max(128)
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var userStatusSchema = z.enum(["ACTIVE", "INACTIVE", "SUSPENDED"]);
var updateUserSchema = z.object({
  email: z.string().email().optional(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  role: z.enum([
    "SUPER_ADMIN",
    "PLATFORM_SUPPORT",
    "PLATFORM_DEVELOPER",
    "COMPETITION_DIRECTOR",
    "CHIEF_JUDGE",
    "JUDGE",
    "SCOREKEEPER",
    "LAUNCH_MARSHAL",
    "GOAL_MARSHAL",
    "ANNOUNCER",
    "DISPLAY_OPERATOR",
    "PUBLIC_USER"
  ]).optional(),
  phone: z.string().optional().nullable(),
  status: userStatusSchema.optional()
});
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("asc")
});
var listUsersQuerySchema = paginationSchema.extend({
  status: z.union([userStatusSchema, z.literal("ALL")]).default("ACTIVE")
});
var orgSlugSchema = z.string().min(2).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be lowercase alphanumeric with hyphens");
var hexColorSchema = z.union([
  z.string().regex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, "Must be a hex color"),
  z.literal(""),
  z.null(),
  z.undefined()
]).transform((v) => v == null || v === "" ? void 0 : v);
var organizationStatusSchema = z.enum(["ACTIVE", "INACTIVE", "ARCHIVED"]);
var organizationPlanSchema = z.enum(["FREE", "STARTER", "PROFESSIONAL", "ENTERPRISE"]);
var createOrganizationSchema = z.object({
  name: z.string().min(2).max(200),
  shortName: z.string().min(1).max(50),
  slug: orgSlugSchema,
  description: optionalString,
  website: optionalString,
  email: z.union([z.string().email(), z.null(), z.undefined(), z.literal("")]).transform((v) => v == null || v === "" ? void 0 : v).optional(),
  phone: optionalString,
  address: optionalString,
  city: optionalString,
  state: optionalString,
  country: optionalString,
  timezone: z.string().min(1).max(80).default("UTC"),
  currency: z.string().min(3).max(3).default("USD"),
  primaryColor: hexColorSchema,
  secondaryColor: hexColorSchema,
  accentColor: hexColorSchema,
  brandingJson: z.record(z.unknown()).optional(),
  defaultRuleProfile: z.enum(["FAI_2022", "FAI_FUTURE", "NPHA_LOCAL", "CUSTOM"]).default("FAI_2022"),
  plan: organizationPlanSchema.default("FREE"),
  featureFlags: z.record(z.unknown()).optional(),
  maxCompetitions: z.number().int().min(1).max(1e4).default(10),
  maxUsers: z.number().int().min(1).max(1e5).default(25)
});
var updateOrganizationSchema = createOrganizationSchema.partial().extend({
  logoUrl: optionalString,
  licenseKey: optionalString
});
var updateOrganizationStatusSchema = z.object({
  status: organizationStatusSchema,
  isActive: z.boolean().optional()
});
var organizationSettingsSchema = z.object({
  generalJson: z.record(z.unknown()).nullable().optional(),
  competitionDefaultsJson: z.record(z.unknown()).nullable().optional(),
  printingDefaultsJson: z.record(z.unknown()).nullable().optional(),
  displayDefaultsJson: z.record(z.unknown()).nullable().optional(),
  certificatesJson: z.record(z.unknown()).nullable().optional(),
  reportsJson: z.record(z.unknown()).nullable().optional(),
  ruleProfileJson: z.record(z.unknown()).nullable().optional(),
  notificationDefaultsJson: z.record(z.unknown()).nullable().optional()
});
var listOrganizationsQuerySchema = paginationSchema.extend({
  status: organizationStatusSchema.optional(),
  isActive: z.union([z.boolean(), z.enum(["true", "false"])]).optional().transform((v) => {
    if (v === void 0) return void 0;
    if (typeof v === "boolean") return v;
    return v === "true";
  })
});
var selectOrganizationSchema = z.object({
  organizationId: z.string().min(1)
});
var orgRoleSchema = z.enum([
  "ORGANIZATION_OWNER",
  "CHIEF_JUDGE",
  "MEET_DIRECTOR",
  "SCORER",
  "JUDGE",
  "ANNOUNCER",
  "DISPLAY_OPERATOR",
  "LAUNCH_MARSHAL",
  "GOAL_MARSHAL",
  "REGISTRATION_OFFICER",
  "VIEWER"
]);
var inviteOrganizationMemberSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  password: z.string().min(8).optional(),
  role: orgRoleSchema.default("VIEWER")
});
var updateOrganizationMemberSchema = z.object({
  role: orgRoleSchema.optional(),
  customRoleId: z.string().min(1).nullable().optional(),
  status: z.enum(["INVITED", "ACTIVE", "INACTIVE", "SUSPENDED"]).optional()
});
var createOrganizationRoleSchema = z.object({
  key: z.string().min(2).max(80).regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, "Use lowercase snake_case keys"),
  name: z.string().min(2).max(120),
  description: z.string().max(500).optional(),
  permissions: z.array(z.string().min(1)).min(1),
  basedOnOrgRole: orgRoleSchema.optional()
});
var updateOrganizationRoleSchema = createOrganizationRoleSchema.partial();
var SPONSOR_TYPES = [
  "TITLE",
  "PRESENTING",
  "GOLD",
  "SILVER",
  "BRONZE",
  "STANDARD"
];
var sponsorTypeSchema = z.enum(SPONSOR_TYPES);
var PARTNER_LABEL_OPTIONS = ["Sponsors", "Supporters"];
var createSponsorSchema = z.object({
  name: z.string().min(1).max(200),
  /** Omit or null when the competition does not use sponsor tiers */
  type: sponsorTypeSchema.nullable().optional(),
  websiteUrl: optionalString,
  logoUrl: optionalString,
  displayOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional()
});
var updateSponsorSchema = createSponsorSchema.partial();
var OFFICIAL_ROLE_OPTIONS = [
  "Meet Director",
  "Chief Judge",
  "Event Judge",
  "CIVL Steward",
  "Event Director",
  "Assistant Event Director",
  "Assistant Meet Director",
  "Safety Director",
  "Target Judge",
  "Launch Marshal",
  "Assistant Launch Marshal",
  "Scorer",
  "Recorder",
  "Wind Monitor",
  "Video Recorder"
];
var LEGACY_OFFICIAL_ROLE_LABELS = {
  judge: "Target Judge",
  scorekeeper: "Scorer",
  "safety officer": "Safety Director"
};
function officialRoleLabel(role) {
  const normalized = role.trim().toLowerCase();
  return LEGACY_OFFICIAL_ROLE_LABELS[normalized] ?? role.trim();
}
function officialRoleRank(role) {
  const label = officialRoleLabel(role).toLowerCase();
  const idx = OFFICIAL_ROLE_OPTIONS.findIndex((r) => r.toLowerCase() === label);
  return idx === -1 ? OFFICIAL_ROLE_OPTIONS.length : idx;
}
function compareOfficials(a, b) {
  const byRole = officialRoleRank(a.role) - officialRoleRank(b.role);
  if (byRole !== 0) return byRole;
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
  return a.name.localeCompare(b.name, void 0, { sensitivity: "base" });
}
var createOfficialBaseSchema = z.object({
  name: z.string().min(1).max(200).optional().or(z.literal("")).transform((v) => v || void 0),
  role: z.string().min(1).max(120),
  /** Reuse existing Person from the directory. */
  personId: optionalString,
  competitionRole: z.enum([
    "PILOT",
    "CHIEF_JUDGE",
    "TARGET_JUDGE",
    "JUDGE",
    "MEET_DIRECTOR",
    "SCORER",
    "ANNOUNCER",
    "DISPLAY_OPERATOR",
    "LAUNCH_MARSHAL",
    "GOAL_MARSHAL",
    "REGISTRATION_OFFICER",
    "SAFETY_DIRECTOR",
    "TECHNICAL_DELEGATE",
    "VIEWER",
    "OTHER"
  ]).optional(),
  imageUrl: optionalString,
  phone: optionalString,
  email: z.preprocess(
    (v) => v === "" || v === null || v === void 0 ? void 0 : v,
    z.string().email().optional()
  ),
  displayOrder: z.number().int().min(0).optional(),
  isPublic: z.boolean().optional()
});
var createOfficialSchema = createOfficialBaseSchema.superRefine((val, ctx) => {
  if (!val.personId && !val.name?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Name is required",
      path: ["name"]
    });
  }
});
var updateOfficialSchema = createOfficialBaseSchema.partial();
var partnersDisplaySettingsSchema = z.object({
  partnersLabel: z.string().min(1).max(40).optional(),
  partnerTiersEnabled: z.boolean().optional()
});
var createProtestSchema = z.object({
  pilotNumber: z.coerce.number().int().min(0).max(99999),
  reason: z.string().trim().min(1, "Say why the protest was made").max(4e3),
  outcome: z.string().trim().min(1, "Record the outcome").max(2e3)
});
var updateProtestSchema = createProtestSchema.partial();
var richHtml = z.union([z.string(), z.null()]).optional().transform((v) => v === void 0 ? void 0 : v);
var mapSiteKindSchema = z.enum(["HQ", "TAKEOFF", "LANDING"]);
var mapSiteSchema = z.object({
  kind: mapSiteKindSchema,
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180)
});
var MAP_SITE_ORDER = ["HQ", "TAKEOFF", "LANDING"];
var MAP_SITE_LABELS = {
  HQ: "HQ",
  TAKEOFF: "Takeoff",
  LANDING: "Landing"
};
var MAP_SITE_COLORS = {
  HQ: "#2563eb",
  TAKEOFF: "#16a34a",
  LANDING: "#ea580c"
};
function parseMapSites(value) {
  if (!Array.isArray(value)) return [];
  const seen = /* @__PURE__ */ new Set();
  const sites = [];
  for (const item of value) {
    const parsed = mapSiteSchema.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.kind)) continue;
    seen.add(parsed.data.kind);
    sites.push(parsed.data);
  }
  return sites.sort((a, b) => MAP_SITE_ORDER.indexOf(a.kind) - MAP_SITE_ORDER.indexOf(b.kind));
}
var updateCompetitionInfoSchema = z.object({
  aboutHtml: richHtml,
  dailyScheduleHtml: richHtml,
  selectionRulesHtml: richHtml,
  entryFeePaymentHtml: richHtml,
  flyingSiteHtml: richHtml,
  travelInfoHtml: richHtml,
  mapLabel: optionalString,
  mapZoom: z.number().int().min(1).max(19).optional(),
  /** Event map coordinates (stored on Competition). HQ is copied here as well. */
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  location: optionalString,
  mapSites: z.array(mapSiteSchema).max(3).optional().refine(
    (sites) => !sites || new Set(sites.map((site) => site.kind)).size === sites.length,
    "Each map site can only be set once"
  )
});
var createGalleryImageSchema = z.object({
  caption: optionalString,
  displayOrder: z.number().int().min(0).optional(),
  /** When creating from an already-uploaded URL (optional — usual path is multipart upload). */
  url: optionalString
});
var updateGalleryImageSchema = z.object({
  caption: optionalString,
  displayOrder: z.number().int().min(0).optional()
});
var competitionLinkKindSchema = z.enum(["EVENT", "REGISTRATION"]);
var createCompetitionLinkSchema = z.object({
  label: z.string().min(1).max(200),
  url: z.string().url().max(2e3),
  displayOrder: z.number().int().min(0).optional(),
  /** Event brochure links stay on the event page. Registration links are shown with a QR code. */
  kind: competitionLinkKindSchema.optional()
});
var updateCompetitionLinkSchema = createCompetitionLinkSchema.partial();
var createCompetitionContactSchema = z.object({
  name: z.string().min(1).max(200),
  role: z.string().min(1).max(120),
  phone: optionalString,
  email: z.preprocess(
    (v) => v === "" || v === null || v === void 0 ? void 0 : v,
    z.string().email().optional()
  ),
  displayOrder: z.number().int().min(0).optional(),
  isPublic: z.boolean().optional()
});
var updateCompetitionContactSchema = createCompetitionContactSchema.partial();
var updateCompetitionSchema = createCompetitionSchema.omit({ organizationId: true, maximumScoreCm: true }).partial();
var updateMaxRoundsSchema = z.object({
  maxRounds: z.number().int().min(1).max(30)
});
function isEmptyHtml(html) {
  if (html == null || html.trim() === "") return true;
  const text = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/&#\d+;/g, " ").replace(/&[a-z]+;/gi, " ").replace(/\s+/g, " ").trim();
  return text.length === 0;
}
var competitionRoleSchema = z.enum([
  "PILOT",
  "CHIEF_JUDGE",
  "TARGET_JUDGE",
  "JUDGE",
  "MEET_DIRECTOR",
  "SCORER",
  "ANNOUNCER",
  "DISPLAY_OPERATOR",
  "LAUNCH_MARSHAL",
  "GOAL_MARSHAL",
  "REGISTRATION_OFFICER",
  "SAFETY_DIRECTOR",
  "TECHNICAL_DELEGATE",
  "VIEWER",
  "OTHER"
]);
var createPersonSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  middleName: optionalString,
  preferredName: optionalString,
  displayName: optionalString,
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).default("MALE"),
  dateOfBirth: z.preprocess(
    (v) => v == null || v === "" ? void 0 : v,
    z.union([z.string().datetime(), z.coerce.date()]).optional()
  ),
  nationalityCountryId: optionalString,
  nationality: optionalString,
  photoUrl: optionalString,
  civlId: optionalString,
  faiLicenseNumber: optionalString,
  faiLicenseExpiry: z.preprocess(
    (v) => v == null || v === "" ? void 0 : v,
    z.union([z.string().datetime(), z.coerce.date()]).optional()
  ),
  email: z.preprocess(
    (v) => v === "" || v === null || v === void 0 ? void 0 : v,
    z.string().email().optional()
  ),
  phone: optionalString,
  visibility: z.enum(["PRIVATE", "ORGANIZATIONS_ONLY", "PUBLIC"]).optional(),
  forceCreate: z.boolean().optional()
});
var clearableString = (max = 200) => z.union([z.string().max(max), z.null()]).optional().transform((value) => {
  if (value === void 0) return void 0;
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
});
var clearableDate = z.preprocess(
  (value) => value === "" ? null : value,
  z.union([z.string().datetime(), z.coerce.date()]).nullable().optional()
);
var updatePersonSchema = z.object({
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().trim().min(1).max(100).optional(),
  middleName: clearableString(100),
  preferredName: clearableString(100),
  displayName: clearableString(100),
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).optional(),
  dateOfBirth: clearableDate,
  nationalityCountryId: clearableString(),
  nationality: clearableString(),
  photoUrl: clearableString(2e3),
  civlId: clearableString(),
  faiLicenseNumber: clearableString(),
  faiLicenseExpiry: clearableDate,
  email: z.union([z.string().email(), z.literal(""), z.null()]).optional().transform((value) => {
    if (value === void 0) return void 0;
    if (value === null || value.trim() === "") return null;
    return value.trim().toLowerCase();
  }),
  phone: clearableString(40),
  visibility: z.enum(["PRIVATE", "ORGANIZATIONS_ONLY", "PUBLIC"]).optional()
});
var queryFlagSchema = z.enum(["true", "false"]).optional().transform((value) => value === void 0 ? void 0 : value === "true");
var listPeopleQuerySchema = paginationSchema.extend({
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  q: z.string().optional(),
  civlId: z.string().optional(),
  aeroJudgeId: z.string().optional(),
  /** When set, results note people who already have a pilot entry in this competition. */
  competitionId: z.string().min(1).optional(),
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).optional(),
  nationalityCountryId: z.string().optional(),
  status: z.enum(["ACTIVE", "ARCHIVED", "MERGED", "ALL"]).optional(),
  /** Only people who have at least one competition pilot record. */
  pilotsOnly: queryFlagSchema,
  /** Include private contact fields (email, phone) for operators. */
  includeContact: queryFlagSchema
});
var matchPersonSchema = z.object({
  aeroJudgeId: optionalString,
  civlId: optionalString,
  faiLicenseNumber: optionalString,
  email: optionalString,
  firstName: optionalString,
  lastName: optionalString,
  nationalityCountryId: optionalString,
  query: optionalString
});
var mergePersonSchema = z.object({
  duplicatePersonId: z.string().min(1)
});
var requestProfileClaimSchema = z.object({
  verificationMethod: z.string().min(1).max(120)
});
var linkUserToPersonSchema = z.object({
  userId: z.string().min(1).optional(),
  userEmail: z.string().email().optional()
}).refine((v) => Boolean(v.userId?.trim() || v.userEmail?.trim()), {
  message: "userId or userEmail is required"
});

// src/constants/index.ts
var PILOT_STATUSES = [
  "REGISTERED",
  "CONFIRMED",
  "CHECKED_IN",
  "ACTIVE",
  "REJECTED",
  "WITHDRAWN",
  "DISQUALIFIED",
  "DNS"
];
var COMPETING_PILOT_STATUSES = [
  "CONFIRMED",
  "CHECKED_IN",
  "ACTIVE"
];
var PENDING_PILOT_STATUSES = ["REGISTERED"];
var STARTED_ROUND_STATUSES = [
  "OPEN",
  "ACTIVE",
  "PAUSED",
  "CLOSED",
  "PENDING_APPROVAL",
  "APPROVED",
  "LOCKED"
];
var PERMISSIONS = {
  "competition:create": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "competition:update": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "competition:delete": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "competition:publish": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "pilot:manage": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "SCOREKEEPER"],
  "team:manage": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "SCOREKEEPER"],
  "round:manage": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "CHIEF_JUDGE", "JUDGE"],
  "round:start": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "CHIEF_JUDGE", "JUDGE"],
  "round:close": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "CHIEF_JUDGE", "JUDGE"],
  "score:enter": ["SUPER_ADMIN", "CHIEF_JUDGE", "JUDGE", "SCOREKEEPER"],
  "score:confirm": ["SUPER_ADMIN", "CHIEF_JUDGE", "SCOREKEEPER"],
  "score:approve_chief": ["SUPER_ADMIN", "CHIEF_JUDGE", "COMPETITION_DIRECTOR"],
  "score:approve_director": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "results:publish": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "CHIEF_JUDGE"],
  "print:generate": [
    "SUPER_ADMIN",
    "COMPETITION_DIRECTOR",
    "CHIEF_JUDGE",
    "SCOREKEEPER",
    "DISPLAY_OPERATOR"
  ],
  "print:approve": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "CHIEF_JUDGE"],
  "user:manage": ["SUPER_ADMIN"],
  "organization:read": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "PLATFORM_SUPPORT"],
  "organization:manage": ["SUPER_ADMIN"],
  "organization:members": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "organization:roles": ["SUPER_ADMIN", "COMPETITION_DIRECTOR"],
  "platform:organizations": ["SUPER_ADMIN"],
  "platform:licenses": ["SUPER_ADMIN"],
  "platform:analytics": ["SUPER_ADMIN", "PLATFORM_SUPPORT"],
  "display:control": ["SUPER_ADMIN", "DISPLAY_OPERATOR", "COMPETITION_DIRECTOR"],
  "announce": ["SUPER_ADMIN", "ANNOUNCER", "COMPETITION_DIRECTOR"],
  "feed:manage": ["SUPER_ADMIN", "CHIEF_JUDGE"],
  "weather:update": [
    "SUPER_ADMIN",
    "LAUNCH_MARSHAL",
    "GOAL_MARSHAL",
    "CHIEF_JUDGE",
    "COMPETITION_DIRECTOR",
    "JUDGE",
    "SCOREKEEPER"
  ],
  "audit:view": ["SUPER_ADMIN", "COMPETITION_DIRECTOR", "CHIEF_JUDGE"]
};
var ALL_PERMISSIONS = Object.keys(PERMISSIONS);
var PLATFORM_ROLES = [
  "SUPER_ADMIN",
  "PLATFORM_SUPPORT",
  "PLATFORM_DEVELOPER"
];
var ORG_ROLES = [
  "ORGANIZATION_OWNER",
  "CHIEF_JUDGE",
  "MEET_DIRECTOR",
  "SCORER",
  "JUDGE",
  "ANNOUNCER",
  "DISPLAY_OPERATOR",
  "LAUNCH_MARSHAL",
  "GOAL_MARSHAL",
  "REGISTRATION_OFFICER",
  "VIEWER"
];
var VIEWER_PERMISSIONS = ["organization:read"];
var JUDGE_PERMISSIONS = [
  "score:enter",
  "round:manage",
  "round:start",
  "round:close",
  // Round score / standings PDFs from Admin → Rounds (and Reports page).
  "print:generate",
  "weather:update"
];
var SCORER_PERMISSIONS = [
  ...VIEWER_PERMISSIONS,
  ...JUDGE_PERMISSIONS,
  "score:confirm",
  "pilot:manage",
  "team:manage",
  "print:generate"
];
var CHIEF_JUDGE_PERMISSIONS = [
  ...SCORER_PERMISSIONS,
  "round:manage",
  "round:start",
  "round:close",
  "score:approve_chief",
  "results:publish",
  "print:approve",
  "weather:update",
  "feed:manage",
  "audit:view"
];
var MEET_DIRECTOR_PERMISSIONS = [
  ...VIEWER_PERMISSIONS,
  "competition:create",
  "competition:update",
  "competition:publish",
  "pilot:manage",
  "team:manage",
  "round:manage",
  "round:start",
  "round:close",
  "score:approve_chief",
  "score:approve_director",
  "results:publish",
  "print:generate",
  "print:approve",
  "organization:members",
  "organization:roles",
  "display:control",
  "announce",
  "weather:update",
  "audit:view"
];
var OWNER_PERMISSIONS = [
  ...MEET_DIRECTOR_PERMISSIONS,
  "competition:delete",
  "organization:manage",
  "score:enter",
  "score:confirm",
  "score:approve_chief"
];
var SYSTEM_ORG_ROLE_DEFINITIONS = {
  ORGANIZATION_OWNER: {
    key: "ORGANIZATION_OWNER",
    name: "Organization Owner",
    description: "Full control within the organization",
    permissions: OWNER_PERMISSIONS
  },
  MEET_DIRECTOR: {
    key: "MEET_DIRECTOR",
    name: "Meet Director",
    description: "Competition director / meet director",
    permissions: MEET_DIRECTOR_PERMISSIONS
  },
  CHIEF_JUDGE: {
    key: "CHIEF_JUDGE",
    name: "Chief Judge",
    description: "Scoring oversight and chief approvals",
    permissions: CHIEF_JUDGE_PERMISSIONS
  },
  SCORER: {
    key: "SCORER",
    name: "Scorer",
    description: "Score confirmation and registration support",
    permissions: SCORER_PERMISSIONS
  },
  JUDGE: {
    key: "JUDGE",
    name: "Judge",
    description: "Enter flight scores",
    permissions: JUDGE_PERMISSIONS
  },
  ANNOUNCER: {
    key: "ANNOUNCER",
    name: "Announcer",
    description: "Live announcements",
    permissions: [...VIEWER_PERMISSIONS, "announce"]
  },
  DISPLAY_OPERATOR: {
    key: "DISPLAY_OPERATOR",
    name: "Display Operator",
    description: "Venue display control",
    permissions: [...VIEWER_PERMISSIONS, "display:control", "print:generate"]
  },
  LAUNCH_MARSHAL: {
    key: "LAUNCH_MARSHAL",
    name: "Launch Marshal",
    description: "Launch / weather updates",
    permissions: [...VIEWER_PERMISSIONS, "weather:update"]
  },
  GOAL_MARSHAL: {
    key: "GOAL_MARSHAL",
    name: "Goal Marshal",
    description: "Goal / weather updates",
    permissions: [...VIEWER_PERMISSIONS, "weather:update"]
  },
  REGISTRATION_OFFICER: {
    key: "REGISTRATION_OFFICER",
    name: "Registration Officer",
    description: "Pilot and team registration",
    permissions: [...VIEWER_PERMISSIONS, "pilot:manage", "team:manage"]
  },
  VIEWER: {
    key: "VIEWER",
    name: "Viewer",
    description: "Read-only organization access",
    permissions: VIEWER_PERMISSIONS
  }
};
var ORG_PERMISSIONS = (() => {
  const inverted = {};
  for (const permission of ALL_PERMISSIONS) {
    inverted[permission] = [];
  }
  for (const def of Object.values(SYSTEM_ORG_ROLE_DEFINITIONS)) {
    for (const permission of def.permissions) {
      inverted[permission].push(def.key);
    }
  }
  return inverted;
})();
var PLATFORM_PERMISSIONS = {
  "platform:organizations": ["SUPER_ADMIN"],
  "platform:licenses": ["SUPER_ADMIN"],
  "platform:analytics": ["SUPER_ADMIN", "PLATFORM_SUPPORT"],
  "user:manage": ["SUPER_ADMIN"],
  /** Org chief judges receive this from their role bundle; super admins receive it here. */
  "feed:manage": ["SUPER_ADMIN", "CHIEF_JUDGE"]
};
function isPlatformRole(role) {
  return PLATFORM_ROLES.includes(role);
}
function hasPermission(role, permission) {
  const platformAllowed = PLATFORM_PERMISSIONS[permission];
  if (platformAllowed?.includes(role)) return true;
  const allowed = PERMISSIONS[permission];
  return allowed.includes(role);
}
function getPermissionsForOrgRole(orgRole) {
  return SYSTEM_ORG_ROLE_DEFINITIONS[orgRole]?.permissions ?? VIEWER_PERMISSIONS;
}
function roleHasPermission(permissions, permission) {
  return permissions.includes(permission);
}
function hasOrgPermission(orgRole, permission) {
  return roleHasPermission(getPermissionsForOrgRole(orgRole), permission);
}
function hasEffectivePermission(input) {
  const {
    platformRole,
    orgRole,
    permissions,
    permission,
    allowLegacyGlobalRole = true
  } = input;
  if (permissions && roleHasPermission(permissions, permission)) return true;
  if (!permissions && orgRole && hasOrgPermission(orgRole, permission)) return true;
  const platformAllowed = PLATFORM_PERMISSIONS[permission];
  if (platformAllowed?.includes(platformRole)) return true;
  if (allowLegacyGlobalRole && !orgRole && !permissions && !isPlatformRole(platformRole) && !isPlatformOnlyPermission(permission) && hasPermission(platformRole, permission)) {
    return true;
  }
  return false;
}
function isPlatformOnlyPermission(permission) {
  return permission === "platform:organizations" || permission === "platform:licenses" || permission === "platform:analytics" || permission === "user:manage";
}
function mapLegacyRoleToOrgRole(role) {
  switch (role) {
    case "SUPER_ADMIN":
      return "ORGANIZATION_OWNER";
    case "COMPETITION_DIRECTOR":
      return "MEET_DIRECTOR";
    case "CHIEF_JUDGE":
      return "CHIEF_JUDGE";
    case "SCOREKEEPER":
      return "SCORER";
    case "JUDGE":
      return "JUDGE";
    case "ANNOUNCER":
      return "ANNOUNCER";
    case "DISPLAY_OPERATOR":
      return "DISPLAY_OPERATOR";
    case "LAUNCH_MARSHAL":
      return "LAUNCH_MARSHAL";
    case "GOAL_MARSHAL":
      return "GOAL_MARSHAL";
    default:
      return "VIEWER";
  }
}
var ADMIN_HOME_PERMISSIONS = [
  "score:confirm",
  "competition:create",
  "competition:update",
  "competition:publish",
  "competition:delete",
  "pilot:manage",
  "team:manage",
  "results:publish",
  "print:generate",
  "print:approve",
  "organization:manage",
  "organization:members",
  "organization:roles",
  "audit:view",
  "score:approve_chief",
  "score:approve_director",
  "display:control",
  "platform:organizations",
  "platform:licenses",
  "platform:analytics",
  "user:manage"
];
function getPreferredStaffApp(input) {
  const permissions = input.permissions ?? [];
  if (input.orgRole === "JUDGE") return "judge";
  const canEnterScores = roleHasPermission(permissions, "score:enter");
  if (!canEnterScores) return "admin";
  const hasAdminHomeWork = ADMIN_HOME_PERMISSIONS.some(
    (permission) => roleHasPermission(permissions, permission)
  );
  return hasAdminHomeWork ? "either" : "judge";
}
function staffAppRedirectTarget(currentApp, preferred) {
  if (preferred === "either" || preferred === currentApp) return null;
  return preferred;
}
var SOCKET_ROOMS = {
  competition: (id) => `competition:${id}`,
  round: (id) => `round:${id}`,
  display: (id) => `display:${id}`,
  public: (slug) => `public:${slug}`
};
var API_VERSION = "v1";
var DEFAULT_PAGE_SIZE = 50;
var MAX_PAGE_SIZE = 200;
var ORGANIZATION_HEADER = "x-organization-id";
export {
  ALL_PERMISSIONS,
  API_VERSION,
  COMPETING_PILOT_STATUSES,
  DEFAULT_FAI_2022_RULES,
  DEFAULT_PAGE_SIZE,
  MAP_SITE_COLORS,
  MAP_SITE_LABELS,
  MAP_SITE_ORDER,
  MAX_PAGE_SIZE,
  OFFICIAL_ROLE_OPTIONS,
  ORGANIZATION_HEADER,
  ORG_PERMISSIONS,
  ORG_ROLES,
  PARTNER_LABEL_OPTIONS,
  PENDING_PILOT_STATUSES,
  PERMISSIONS,
  PILOT_STATUSES,
  PLATFORM_PERMISSIONS,
  PLATFORM_ROLES,
  ROLES,
  SOCKET_ROOMS,
  SPONSOR_TYPES,
  STARTED_ROUND_STATUSES,
  SYSTEM_ORG_ROLE_DEFINITIONS,
  authenticatedPilotRegistrationSchema,
  bulkPilotActionSchema,
  claimPersonByIdentitySchema,
  compareOfficials,
  competitionLinkKindSchema,
  competitionRoleSchema,
  createCompetitionContactSchema,
  createCompetitionLinkSchema,
  createCompetitionSchema,
  createGalleryImageSchema,
  createOfficialBaseSchema,
  createOfficialSchema,
  createOrganizationRoleSchema,
  createOrganizationSchema,
  createPersonSchema,
  createPilotBaseSchema,
  createPilotSchema,
  createProtestSchema,
  createRoundSchema,
  createSponsorSchema,
  createTeamSchema,
  createUserSchema,
  enterScoreSchema,
  faiCategoryLabel,
  forgotPasswordSchema,
  getPermissionsForOrgRole,
  getPreferredStaffApp,
  hasEffectivePermission,
  hasOrgPermission,
  hasPermission,
  inviteOrganizationMemberSchema,
  isEmptyHtml,
  isPlatformOnlyPermission,
  isPlatformRole,
  linkUserToPersonSchema,
  listOrganizationsQuerySchema,
  listPeopleQuerySchema,
  listUsersQuerySchema,
  loginSchema,
  mapLegacyRoleToOrgRole,
  mapSiteKindSchema,
  mapSiteSchema,
  matchPersonSchema,
  mergePersonSchema,
  officialRoleLabel,
  officialRoleRank,
  orgRoleSchema,
  organizationPlanSchema,
  organizationSettingsSchema,
  organizationStatusSchema,
  paginationSchema,
  parseMapSites,
  partnersDisplaySettingsSchema,
  pilotStatusSchema,
  publicPilotRegistrationSchema,
  registerParticipantSchema,
  requestProfileClaimSchema,
  resetPasswordSchema,
  roleHasPermission,
  selectOrganizationSchema,
  setUserPasswordSchema,
  sponsorTypeSchema,
  staffAppRedirectTarget,
  updateCompetitionContactSchema,
  updateCompetitionInfoSchema,
  updateCompetitionLinkSchema,
  updateCompetitionSchema,
  updateGalleryImageSchema,
  updateMaxRoundsSchema,
  updateOfficialSchema,
  updateOrganizationMemberSchema,
  updateOrganizationRoleSchema,
  updateOrganizationSchema,
  updateOrganizationStatusSchema,
  updatePersonSchema,
  updatePilotSchema,
  updatePilotStatusSchema,
  updateProtestSchema,
  updateRoundTypeSchema,
  updateSponsorSchema,
  updateTeamSchema,
  updateUserSchema,
  userStatusSchema
};
